"use client";

import { useMemo } from "react";
import type { SurveyQuestion } from "@/types/survey";
import { InputSelect } from "@/design-system";
import { COUNTRY_OPTIONS } from "@/lib/countryOptions";

interface CountryQuestionProps {
  question: SurveyQuestion;
  brandColor?: string;
  isSelected?: (
    question: SurveyQuestion,
    candidate: string | number,
  ) => boolean;
  onAnswerChange?: (question: SurveyQuestion, value: string) => void;
  isPreview?: boolean;
  selectedValue?: string | number;
  onPreviewAnswerChange?: (questionId: string, value: string | number) => void;
  uiStrings?: {
    selectCountry: string;
    countryDisplayLocale: string;
  };
}

export default function CountryQuestion({
  question,
  isSelected,
  onAnswerChange,
  isPreview = false,
  selectedValue,
  onPreviewAnswerChange,
  uiStrings,
}: CountryQuestionProps) {
  const displayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([uiStrings?.countryDisplayLocale ?? "en"], {
        type: "region",
      });
    } catch {
      return null;
    }
  }, [uiStrings?.countryDisplayLocale]);
  const countryOptions = useMemo(
    () =>
      COUNTRY_OPTIONS.map((option) => ({
        ...option,
        label: displayNames?.of(option.code) ?? option.label,
      })),
    [displayNames],
  );
  const selected = isPreview
    ? String(selectedValue ?? "")
    : COUNTRY_OPTIONS.find((option) =>
        isSelected ? isSelected(question, option.value) : false,
      )?.value ?? "";

  return (
    <div className="mb-4">
      <InputSelect
        label={question.text || (uiStrings?.selectCountry ?? "Country")}
        value={selected || null}
        placeholder={uiStrings?.selectCountry ?? "Select a country"}
        searchable
        options={countryOptions}
        onChange={(event) => {
          const next = event.target.value;
          if (isPreview && onPreviewAnswerChange) {
            onPreviewAnswerChange(question.id, next);
            return;
          }
          if (onAnswerChange && next) {
            onAnswerChange(question, next);
          }
        }}
      />
    </div>
  );
}
